/**
 * NavListItem — canonical role-tinted nav item for the three layouts.
 * Active accent by role: user → primary.main, partner → primary.light,
 * admin → primary.dark. 44pt min tap target (WCAG 2.5.5 AAA);
 * aria-current="page" set automatically when `active`.
 */

import type { ReactNode } from 'react';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import type { SxProps, Theme } from '@mui/material/styles';
import { colors, designTokens, motion } from '../../theme/theme';

export type NavRole = 'user' | 'partner' | 'admin';

export interface NavListItemProps {
  /** The destination path (used for `aria-current` semantics + click handler). */
  to: string;
  /** Visible nav label. */
  label: ReactNode;
  /** Material-UI icon node. Sized 24px by default. */
  icon: ReactNode;
  /** Role tint — controls the active-state accent. */
  role: NavRole;
  /** True when the current route matches this item — paints the active state + sets aria-current. */
  active?: boolean;
  /** Click / tap handler. Fires on Enter/Space too via MUI defaults. */
  onNavigate?: () => void;
  /** Optional escape hatch for one-off overrides (rarely needed). */
  sx?: SxProps<Theme>;
  /** Density. `compact` shrinks vertical padding (used in role-dense admin sections). Default: `regular`. */
  density?: 'regular' | 'compact';
}

/**
 * Maps the role to its accent colour. Partner and admin stay inside the coral
 * family per founder spec ("subtle role tints") so the brand never feels split
 * — partner skews warmer (light), admin skews redder (dark).
 */
function roleAccent(role: NavRole): string {
  if (role === 'partner') return colors.primary.light; // amber-warmer
  if (role === 'admin') return colors.primary.dark; // red-deeper
  return colors.primary.main; // user — pure coral
}

export default function NavListItem({
  to,
  label,
  icon,
  role,
  active = false,
  onNavigate,
  sx,
  density = 'regular',
}: NavListItemProps) {
  const accent = roleAccent(role);
  const compact = density === 'compact';

  const baseSx: SxProps<Theme> = {
    minHeight: 44,
    borderRadius: `${designTokens.radius.md}px`,
    mx: 1,
    mb: 0.5,
    px: 2,
    py: compact ? 0.75 : 1.25,
    color: active ? '#FFFFFF' : colors.neutral[500],
    transition: `background-color ${motion.duration.fast} ${motion.easing.standard}, color ${motion.duration.fast} ${motion.easing.standard}`,
    '& .MuiListItemIcon-root': {
      color: active ? accent : colors.neutral[500],
      minWidth: 36,
      transition: `color ${motion.duration.fast} ${motion.easing.standard}`,
    },
    '&:hover': {
      bgcolor: 'rgba(255,255,255,0.04)',
      color: active ? '#FFFFFF' : colors.neutral[700],
      '& .MuiListItemIcon-root': {
        color: active ? accent : colors.neutral[700],
      },
    },
    // Selected state — coral-tinted bg, white label, role-coloured icon.
    // Inline `&.Mui-selected` to override the previous per-layout styles.
    '&.Mui-selected': {
      bgcolor: `${accent}1F`, // ~12% alpha — token-equivalent of colors.primary.subtle
      color: '#FFFFFF',
      '&:hover': {
        bgcolor: `${accent}29`, // ~16% alpha — slightly stronger on hover
      },
    },
    '&.Mui-focusVisible': {
      outline: 'none',
      boxShadow: designTokens.shadow.focus,
    },
  };

  return (
    <ListItemButton
      selected={active}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      data-nav-target={to}
      sx={[baseSx, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
    >
      <ListItemIcon>{icon}</ListItemIcon>
      <ListItemText
        primary={label}
        primaryTypographyProps={{
          fontSize: compact ? '0.875rem' : '0.95rem',
          fontWeight: active ? 700 : 500,
          letterSpacing: '0.01em',
        }}
      />
    </ListItemButton>
  );
}
